import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPlus,
  FaCalendarAlt,
  FaChevronUp,
  FaChevronDown,
  FaTrash,
  FaEdit,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const SalesConvertedLeadDetails = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = `bg-${baseColor}-${baseShade}`;

  const [showFilters, setShowFilters] = useState(true);
  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // dropdown data
  const [assetSubTypes, setAssetSubTypes] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);

  // autocomplete lists
  const [enqSuggestions, setEnqSuggestions] = useState([]);
  const [custSuggestions, setCustSuggestions] = useState([]);

  const [loadingDD, setLoadingDD] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  // ✅ Selected rows
  const [selectedIds, setSelectedIds] = useState([]);

  // search form state
  const [searchData, setSearchData] = useState({
    enquiryNumber: "",
    customerName: "",
    paymentMode: "",
    assetSubTypeId: "",
    doB: "",
  });

  // result data
  const [leadsData, setLeadsData] = useState([]);

  // debounce refs
  const enqTimer = useRef(null);
  const custTimer = useRef(null);

  const handleInputChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  const loadDropdowns = async () => {
    try {
      setLoadingDD(true);

      const [subTypesRes, payModesRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/leadListing/assetsubtypes`, {
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/api/leadListing/paymentmodes`, {
          headers: commonHeaders,
        }),
      ]);

      setAssetSubTypes(subTypesRes.data?.data || []);
      setPaymentModes(payModesRes.data?.data || []);
    } catch (err) {
      console.error("Dropdown load error:", err);
      toast.error(err?.response?.data?.message || "Failed to load dropdowns");
      setAssetSubTypes([]);
      setPaymentModes([]);
    } finally {
      setLoadingDD(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
  }, [commonHeaders]);

  // --- Autocomplete: enquiry number ---
  useEffect(() => {
    const q = (searchData.enquiryNumber || "").trim();
    if (enqTimer.current) clearTimeout(enqTimer.current);

    if (!q) {
      setEnqSuggestions([]);
      return;
    }

    enqTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/leadListing/enquiry/autocomplete`,
          {
            params: { q },
            headers: commonHeaders,
          }
        );
        setEnqSuggestions(res.data?.data || []);
      } catch {
        setEnqSuggestions([]);
      }
    }, 250);

    return () => enqTimer.current && clearTimeout(enqTimer.current);
  }, [searchData.enquiryNumber, commonHeaders]);

  // --- Autocomplete: customer name ---
  useEffect(() => {
    const q = (searchData.customerName || "").trim();
    if (custTimer.current) clearTimeout(custTimer.current);

    if (!q) {
      setCustSuggestions([]);
      return;
    }

    custTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/leadListing/customer/autocomplete`,
          {
            params: { q },
            headers: commonHeaders,
          }
        );
        setCustSuggestions(res.data?.data || []);
      } catch {
        setCustSuggestions([]);
      }
    }, 250);

    return () => custTimer.current && clearTimeout(custTimer.current);
  }, [searchData.customerName, commonHeaders]);

  const hasSearchCriteria = useMemo(() => {
    return Object.values(searchData).some((v) => String(v || "").trim() !== "");
  }, [searchData]);

  const doSearch = async () => {
    try {
      if (!hasSearchCriteria) {
        toast.warning("Please enter at least one search criteria!");
        return;
      }

      setLoadingSearch(true);
      setSelectedIds([]);

      const res = await axios.get(`${BASE_URL}/api/leadListing/search`, {
        params: {
          enquiryNumber: searchData.enquiryNumber,
          customerName: searchData.customerName,
          paymentMode: searchData.paymentMode,
          assetSubTypeId: searchData.assetSubTypeId,
          dob: searchData.doB,
        },
        headers: commonHeaders,
      });

      const data = res.data?.data || [];
      setLeadsData(data);

      toast.success(`Found ${data.length} record(s)`);
    } catch (err) {
      console.error("Search error:", err);
      toast.error(err?.response?.data?.message || "Search failed");
      setLeadsData([]);
      setSelectedIds([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const clearFilters = () => {
    setSearchData({
      enquiryNumber: "",
      customerName: "",
      paymentMode: "",
      assetSubTypeId: "",
      doB: "",
    });
    setEnqSuggestions([]);
    setCustSuggestions([]);
    setLeadsData([]);
    setSelectedIds([]);
  };

  const handleAddNew = () => {
    navigate("/Sales/LeadSalesmergeEntry");
  };

  // ✅ checkbox helpers
  const allRowIds = useMemo(
    () => leadsData.map((x) => x.salesLeadId).filter(Boolean),
    [leadsData]
  );

  const isAllSelected =
    allRowIds.length > 0 && selectedIds.length === allRowIds.length;

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedIds([]);
    else setSelectedIds(allRowIds);
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleUpdate = () => {
    if (selectedIds.length === 0) {
      toast.warning("Select one row to update!");
      return;
    }
    if (selectedIds.length > 1) {
      toast.warning("Please select only ONE row to update!");
      return;
    }

    const id = selectedIds[0];
    navigate("/Sales/LeadSalesmergeEntry", { state: { salesLeadId: id } });
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Select at least one row to delete!");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      setLoadingDelete(true);

      await axios.delete(`${BASE_URL}/api/leadListing/deleteSelected`, {
        data: { ids: selectedIds },
        headers: commonHeaders,
      });

      toast.success("Deleted successfully!");
      setLeadsData((prev) =>
        prev.filter((r) => !selectedIds.includes(r.salesLeadId))
      );
      setSelectedIds([]);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setLoadingDelete(false);
    }
  };

  const getPaymentModeColor = (mode) => {
    switch (mode) {
      case "Full Payment":
        return "bg-green-100 text-green-800";
      case "Part Payment":
        return "bg-yellow-100 text-yellow-800";
      case "EMI":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <SalesLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SALES CONVERTED LEAD DETAILS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Sales Converted Lead Details
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={handleUpdate}
                className="flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <FaEdit />
                Update
              </button>

              <button
                onClick={handleDelete}
                disabled={loadingDelete}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  loadingDelete ? "opacity-60 cursor-not-allowed" : ""
                } bg-red-600 hover:bg-red-700 text-white`}
              >
                <FaTrash />
                {loadingDelete ? "Deleting..." : "Delete"}
              </button>

              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={clearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={doSearch}
                    disabled={loadingSearch}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loadingSearch ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    {loadingSearch ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="relative">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Enquiry Number
                  </label>
                  <input
                    type="text"
                    value={searchData.enquiryNumber}
                    onChange={(e) => handleInputChange("enquiryNumber", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                  {enqSuggestions.length > 0 && (
                    <div
                      className={`absolute z-20 mt-1 w-full rounded-lg border shadow-lg max-h-48 overflow-auto ${
                        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                      }`}
                    >
                      {enqSuggestions.map((sug) => (
                        <button
                          type="button"
                          key={sug}
                          onClick={() => {
                            handleInputChange("enquiryNumber", sug);
                            setEnqSuggestions([]);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            isDark ? "hover:bg-gray-700 text-gray-100" : "hover:bg-gray-100 text-gray-800"
                          }`}
                        >
                          {sug}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={searchData.customerName}
                    onChange={(e) => handleInputChange("customerName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                  {custSuggestions.length > 0 && (
                    <div
                      className={`absolute z-20 mt-1 w-full rounded-lg border shadow-lg max-h-48 overflow-auto ${
                        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                      }`}
                    >
                      {custSuggestions.map((sug) => (
                        <button
                          type="button"
                          key={sug}
                          onClick={() => {
                            handleInputChange("customerName", sug);
                            setCustSuggestions([]);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            isDark ? "hover:bg-gray-700 text-gray-100" : "hover:bg-gray-100 text-gray-800"
                          }`}
                        >
                          {sug}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Payment Mode
                  </label>
                  <select
                    value={searchData.paymentMode}
                    onChange={(e) => handleInputChange("paymentMode", e.target.value)}
                    disabled={loadingDD}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {paymentModes.map((m) => (
                      <option key={m.id || m.name} value={m.id || m.name}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Asset Sub Type
                  </label>
                  <select
                    value={searchData.assetSubTypeId}
                    onChange={(e) => handleInputChange("assetSubTypeId", e.target.value)}
                    disabled={loadingDD}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {assetSubTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaCalendarAlt className="inline mr-2" />
                    DoB
                  </label>
                  <input
                    type="date"
                    value={searchData.doB}
                    onChange={(e) => handleInputChange("doB", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold w-12">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Sl No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Enquiry Number</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Customer Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Customer ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Payment Mode</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Date of Booking</th>
                </tr>
              </thead>

              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {loadingSearch ? (
                  <tr>
                    <td colSpan="7" className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : leadsData.length === 0 ? (
                  <tr>
                    <td colSpan="7" className={`px-6 py-10 text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      No Records Found!
                    </td>
                  </tr>
                ) : (
                  leadsData.map((lead, index) => {
                    const id = lead.salesLeadId;
                    const checked = selectedIds.includes(id);

                    return (
                      <tr
                        key={`${id}-${index}`}
                        className={`${
                          index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRow(id)}
                            className="w-4 h-4"
                          />
                        </td>

                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {lead.slNo}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {lead.enquiryNumber}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {lead.customerName}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {lead.customerId}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span
                            className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentModeColor(
                              lead.paymentMode
                            )}`}
                          >
                            {lead.paymentMode}
                          </span>
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {lead.dateOfBooking}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default SalesConvertedLeadDetails;